import {EventBus} from "../../../common/ts/events/EventBus"
import {
    PushManagerSubscriptionCreatedEvent,
    PushManagerSubscriptionStatus
} from "./events"
import CookieService from "../../../common/ts/services/CookieService"
import {CssBreakpoints} from "../pages/CssBreakpoints"

const CUSTOM_BANNER_BLOCKED_COOKIE = 'wpn_banner_blocked'

export class WPNManager {

    private readonly publicVapidKey: string
    private readonly swScriptUrl: string
    private readonly eventBus: EventBus
    private readonly customBanner: HTMLElement | null
    private readonly customBannerAcceptButton: HTMLElement | null
    private readonly customBannerDenyButton: HTMLElement | null
    private readonly cookieService = new CookieService()

    constructor(
        publicVapidKey: string,
        swScriptUrl: string,
        eventBus: EventBus
    ) {
        this.publicVapidKey = publicVapidKey
        this.swScriptUrl = swScriptUrl
        this.eventBus = eventBus
        this.customBanner = document.querySelector('.js-wpn-custom-banner')
        this.customBannerAcceptButton = document.querySelector('.js-wpn-custom-banner-accept')
        if (this.customBannerAcceptButton) {
            this.customBannerAcceptButton.addEventListener('click', () => {
                this.hidePermissionBanner()
                this.handlePermissions()
            })
        }
        this.customBannerDenyButton = document.querySelector('.js-wpn-custom-banner-deny')
        if (this.customBannerDenyButton) {
            this.customBannerDenyButton.addEventListener('click', () => {
                this.hidePermissionBanner()
                this.blockPermissionBanner(1)
            })
        }
    }

    async init() {
        if (this.isActivated()) {
            await this.registerServiceWorker()
            await this.checkPermissions()
        }
    }

    private async checkPermissions() {
        if (this.isFirefox() || (this.isSafari() && !this.isMobile())) {
            this.showPermissionBanner()
        } else if (this.isBrowserCompatibleWithWebPush()) {
            await this.handlePermissions()
        }
    }

    private async handlePermissions() {
        if (Notification.permission === 'default') {
            Notification.requestPermission().then(permission => {
                if (permission === 'granted') {
                    this.pushManagerSubscriptionCreated(PushManagerSubscriptionStatus.ALLOWED)
                } else if (permission === 'denied') {
                } else {
                }
            })
        } else if (Notification.permission === 'granted') {
            await this.pushManagerSubscriptionCreated(PushManagerSubscriptionStatus.GRANTED)
        } else if (Notification.permission === 'denied') {
        }
    }

    private async registerServiceWorker() {
        await navigator.serviceWorker.register(this.swScriptUrl)
    }

    private async pushManagerSubscriptionCreated(status: PushManagerSubscriptionStatus) {
        const subscription = await this.getPushSubscription()
        const key = subscription.getKey("p256dh")
        const auth = subscription.getKey("auth")

        if (key && auth) {
            this.eventBus.emit(new PushManagerSubscriptionCreatedEvent(
                {
                    endpoint: subscription.endpoint,
                    auth: this.arrayBufferToBase64(auth),
                    key: this.arrayBufferToBase64(key),
                    status: status,
                }
            ))
        }
    }

    private async getPushSubscription() {
        const serviceWorkerRegistration = await navigator.serviceWorker.ready
        const subscription = await serviceWorkerRegistration.pushManager.getSubscription()
        if (subscription) return subscription
        return await serviceWorkerRegistration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: this.urlBase64ToUint8Array(this.publicVapidKey),
        })
    }

    private isBrowserCompatibleWithWebPush(): boolean {
        const hasServiceWorker = 'serviceWorker' in navigator
        const hasPushManager = 'PushManager' in window
        const hasNotification = 'Notification' in window
        const hasShowNotification =
            typeof ServiceWorkerRegistration !== 'undefined' &&
            'showNotification' in ServiceWorkerRegistration.prototype
        return (
            hasServiceWorker &&
            hasPushManager &&
            hasNotification &&
            hasShowNotification
        )
    }

    private urlBase64ToUint8Array = (base64String: string) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4)
        const base64 = (base64String + padding)
            .replace(/\-/g, '+')
            .replace(/_/g, '/')

        const rawData = window.atob(base64)
        const outputArray = new Uint8Array(rawData.length)

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i)
        }
        return outputArray
    }

    private arrayBufferToBase64 = (arrayBuffer: ArrayBuffer) => {
        return btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(arrayBuffer))))
    }

    private isMobile(): boolean {
        return !CssBreakpoints.get().isDesktop()
    }

    private showPermissionBanner(): void {
        if (this.customBanner && Notification.permission === 'default' && !CookieService.exists(CUSTOM_BANNER_BLOCKED_COOKIE)) {
            this.customBanner.classList.add('is-visible')
        }
    }

    private hidePermissionBanner(): void {
        if (this.customBanner) {
            this.customBanner.classList.remove('is-visible')
        }
    }

    private blockPermissionBanner(days: Number): void {
        this.cookieService.setExpirableCookie(CUSTOM_BANNER_BLOCKED_COOKIE, 'true', days as number)
    }

    private isActivated(): boolean {
        const params = new URLSearchParams(window.location.search)
        return params.get('PROD-13036') === 'true'
    }

    private isFirefox(): boolean {
        const ua = navigator.userAgent.toLowerCase();
        return ua.includes('firefox') && !ua.includes('seamonkey');
    }

    private isSafari(): boolean {
        const ua = navigator.userAgent.toLowerCase();
        return ua.includes('safari') && !ua.includes('chrome') && !ua.includes('android');
    }
}
